import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useInvalidateAnalysisRuns } from '@/lib/analyses/useAnalysisRuns';
import { useAtom } from 'jotai';
import { Droplets, TriangleAlert } from 'lucide-react';
import { useEffect, useId, useMemo } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { rainfallTrackingAtom } from '@/atoms/rainfall-progress.atom';
import { execute } from '@/graphql/execute';
import type { LayersQuery } from '@/graphql/graphql';
import {
  DURATION_OPTIONS,
  RETURN_YEAR_OPTIONS,
  lookupStorm,
  type DurationLabel,
  type ReturnYears,
} from '@/lib/rainfallStormPresets';
import { useDialog } from '@/providers/DialogProvider';
import { startRainfallAnalysis } from '@/queries/rainfallAnalysis.query';
import { isVectorLayer } from '@/lib/analyses/candidates';
import { useSourceLayerPick } from '@/lib/analyses/useSourceLayerPick';
import { AdvancedSection, AnalysisSection, InputRoleField, OutputSummary } from '@/components/analyses/sections';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NumericField } from '@/components/ui/numeric-field';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AnalysisDialogShell } from '@/components/analyses/AnalysisDialogShell';

type LayerData = LayersQuery['layers'][number];
type LayersResult = { layers: LayerData[] };

// Maximum DEM cell count the worker will accept — mirror of the
// preprocess CELL_CAP constant. Surface-the-error-early in the dialog so
// the user can adjust resolution before paying for a Fargate job that
// will fail in 30 seconds.
const CELL_CAP = 200_000_000;

// Resolution range mirrors the worker's bounds (worker.py: 0.05 ≤ res ≤ 5.0).
// The dialog accepts any number; zod enforces the same range as the worker
// so server-side errors are vanishingly rare.
const MIN_RESOLUTION_M = 0.05;
const MAX_RESOLUTION_M = 5.0;

interface FormData {
  sinkHandling: 'FILL' | 'BREACH';
  resolutionM: number;
  minPondDepthM: number;
  minPondAreaM2: number;
  mode: 'TOPOLOGY' | 'STORM';
  // Storm-only fields. Always present in form state so the inputs stay
  // controlled when the user toggles mode back and forth, but only
  // forwarded to the mutation when mode === 'STORM'.
  stormDepthMm: number;
  stormDurationH: number;
  runoffCoefficient: number;
  name: string;
}

// Common runoff-coefficient presets — surfaced as clickable chips
// beside the C input so users don't need to remember typical values.
// Source: standard hydrology references (e.g. UK SuDS Manual, US ASCE
// Rational Method tables). Values are intentionally conservative
// midpoints — real-world choices vary ±0.05 by surface condition.
const RUNOFF_C_PRESETS: Array<{ labelKey: string; value: number }> = [
  { labelKey: 'rainfallAnalysis.cPresetAsphalt', value: 0.85 },
  { labelKey: 'rainfallAnalysis.cPresetRoof', value: 0.95 },
  { labelKey: 'rainfallAnalysis.cPresetGrass', value: 0.3 },
];

interface Props {
  /** Pre-fill for the source TIN picker (context layer, re-run). D10. */
  sourceLayer?: LayerData;
  projectId: string;
}

/** Units go in the label as "(m)" / "(mm)" in every analysis dialog. */
const withUnit = (label: string, unit: string) => `${label} (${unit})`;

/**
 * Locale-aware "12,345" / "12 345" formatting for the live grid-size
 * estimate. We deliberately avoid scientific notation — planners want to
 * see the actual cell count.
 */
const fmtCount = (n: number): string =>
  Number.isFinite(n) ? n.toLocaleString() : String(n);

/**
 * Project-CRS bbox extent for a VECTOR layer. Reads `layer.bboxNew`
 * (the canonical 3D bbox the rest of the codebase uses — see
 * LayerProperties.tsx, MapView.tsx); `layer.bbox` is the legacy 2D
 * field. `bboxNew.coords` is `[[minX, minY, minZ], [maxX, maxY, maxZ]]`
 * in the layer's CRS. We use only the X/Y components for grid sizing —
 * Z is irrelevant to the DEM cell count.
 *
 * Returns NaN extents when the bbox isn't loaded yet — the dialog
 * shows "—" in that case and lets the user submit anyway (preprocess
 * re-checks server-side).
 */
function bboxExtent(layer: LayerData): { widthM: number; heightM: number } {
  const bboxNew = layer.bboxNew as null | { coords?: number[][] };
  const coords = bboxNew?.coords;
  if (!coords || coords.length < 2 || coords[0].length < 2 || coords[1].length < 2) {
    return { widthM: NaN, heightM: NaN };
  }
  const [minX, minY] = coords[0];
  const [maxX, maxY] = coords[1];
  return { widthM: Math.max(0, maxX - minX), heightM: Math.max(0, maxY - minY) };
}

/**
 * Dialog for kicking off a rainfall (surface hydrology) analysis on a
 * VECTOR (TIN) layer. Body follows the shared config sections (Inputs →
 * Settings → Output → Advanced settings): routing + resolution in
 * Settings (the things every user touches), pond filters under the
 * collapsed Advanced settings.
 *
 * The grid-size estimate updates live as the user types resolution.
 * If the estimate exceeds CELL_CAP we block submission with a clear
 * inline error — same message the preprocess returns server-side, so
 * users who somehow bypass the check still get a recognisable failure.
 */
export function RunRainfallAnalysisDialog({ sourceLayer: initialSourceLayer, projectId }: Props) {
  const { t } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();
  const formId = useId();
  const queryClient = useQueryClient();
  const {
    candidates: sourceCandidates,
    sourceLayer,
    sourceLayerId,
    setSourceLayerId,
  } = useSourceLayerPick(isVectorLayer, initialSourceLayer);

  const formSchema = z
    .object({
      sinkHandling: z.enum(['FILL', 'BREACH']),
      resolutionM: z
        .number()
        .min(MIN_RESOLUTION_M, t('rainfallAnalysis.errors.gridTooLarge', { cells: '∞', cap: CELL_CAP / 1_000_000 }))
        .max(MAX_RESOLUTION_M),
      minPondDepthM: z.number().min(0),
      minPondAreaM2: z.number().min(0),
      mode: z.enum(['TOPOLOGY', 'STORM']),
      stormDepthMm: z.number().min(0),
      stormDurationH: z.number().min(0),
      runoffCoefficient: z.number().min(0).max(1),
      name: z.string().min(1),
    })
    // STORM mode: storm fields must be > 0 (and runoffC > 0). We
    // validate as a refinement rather than per-field min(>0) so the
    // inputs stay valid in TOPOLOGY mode where the values are ignored.
    .refine(
      (v) => v.mode === 'TOPOLOGY' || (v.stormDepthMm > 0 && v.stormDurationH > 0 && v.runoffCoefficient > 0),
      { message: t('rainfallAnalysis.errors.stormFieldsRequired') as string, path: ['mode'] },
    );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      sinkHandling: 'BREACH',
      resolutionM: 0.5,
      minPondDepthM: 0.05,
      minPondAreaM2: 0.5,
      // Default to TOPOLOGY so a one-click run still produces useful
      // output without forcing the user to think about a storm. Users
      // who want quantitative runoff/fill-time numbers flip the toggle.
      mode: 'TOPOLOGY',
      stormDepthMm: 50,
      stormDurationH: 1,
      runoffCoefficient: 0.85,
      name: t('rainfallAnalysis.namePlaceholder', { source: sourceLayer?.name ?? '' }),
    },
  });

  const [, setTrackers] = useAtom(rainfallTrackingAtom);
  // Opens Advanced settings by itself when a pond filter fails validation.
  const { errors } = useFormState({ control: form.control });
  const advancedHasError = !!(errors.minPondDepthM || errors.minPondAreaM2);

  const watchedResolution = form.watch('resolutionM');

  useEffect(() => {
    if (!sourceLayer || form.formState.dirtyFields.name) return;
    const suggested = t('rainfallAnalysis.namePlaceholder', { source: sourceLayer.name });
    if (form.getValues('name') !== suggested) form.setValue('name', suggested, { shouldDirty: false });
  }, [sourceLayer, form, t]);

  // Live grid-size estimate. Memo so we don't recompute on every render
  // unrelated to bbox or resolution.
  const gridEstimate = useMemo(() => {
    const { widthM, heightM } = sourceLayer ? bboxExtent(sourceLayer) : { widthM: NaN, heightM: NaN };
    if (!Number.isFinite(widthM) || !Number.isFinite(heightM)) {
      return { cellCount: NaN, cols: NaN, rows: NaN, exceedsCap: false };
    }
    const res = watchedResolution > 0 ? watchedResolution : 1;
    const cols = Math.ceil(widthM / res);
    const rows = Math.ceil(heightM / res);
    const cellCount = cols * rows;
    return { cellCount, cols, rows, exceedsCap: cellCount > CELL_CAP };
  }, [sourceLayer, watchedResolution]);

  const watchedMode = form.watch('mode');

  const invalidateAnalysisRuns = useInvalidateAnalysisRuns();
  const mutation = useMutation({
    mutationFn: async ({ vars, source }: { vars: FormData; source: LayerData }) =>
      execute(startRainfallAnalysis, {
        input: {
          sourceLayerId: source.id,
          label: vars.name,
          params: {
            sinkHandling: vars.sinkHandling,
            resolutionM: vars.resolutionM,
            minPondDepthM: vars.minPondDepthM,
            minPondAreaM2: vars.minPondAreaM2,
            mode: vars.mode,
            // Forward storm fields only when STORM is selected — sending
            // them in TOPOLOGY mode would be confusing in the saved job
            // payload (the worker ignores them either way).
            ...(vars.mode === 'STORM'
              ? {
                  stormDepthMm: vars.stormDepthMm,
                  stormDurationH: vars.stormDurationH,
                  runoffCoefficient: vars.runoffCoefficient,
                }
              : {}),
          },
        },
      }),
    onSuccess: (data, { source }) => {
      invalidateAnalysisRuns(projectId);
      const batchId = data.startRainfallAnalysis;
      // Open a long-lived loading toast (`duration: Infinity` — won't
      // auto-dismiss) and hand it to the project-shell rainfall progress
      // hook (`useRainfallProgress`), which polls jobs + layers and
      // resolves the toast into either a success or a failure when the
      // run actually finishes server-side. Mirrors `useDeviationProgress`
      // / `useGroundClassifyProgress` exactly so the visible state matches
      // what's happening on the backend rather than what the mutation
      // optimistically returned.
      const toastId = toast.loading(
        t('rainfallAnalysis.runningToast', { name: source.name }),
        { duration: Infinity },
      );
      // Snapshot existing layers derived from this source so the hook
      // can detect "the new ones this run produces" by exclusion. Same
      // disambiguation strategy deviation/ground-classify use.
      const cachedLayers = queryClient.getQueryData<LayersResult>(['layers', projectId]);
      const existingDerivedLayerIds = (cachedLayers?.layers ?? [])
        .filter((l) => {
          const meta = (l.metadata ?? {}) as { derivedFrom?: string };
          return meta.derivedFrom === source.id;
        })
        .map((l) => l.id);
      setTrackers((prev) => [
        ...prev,
        {
          toastId,
          batchId,
          projectId,
          sourceLayerId: source.id,
          sourceLayerName: source.name,
          existingDerivedLayerIds,
        },
      ]);
      queryClient.invalidateQueries({ queryKey: ['batches', projectId] });
      dismissActiveDialog();
    },
    onError: (err: Error) => {
      toast.error(err.message || t('rainfallAnalysis.failedToStart'));
    },
  });

  const submitDisabled =
    mutation.isPending || gridEstimate.exceedsCap || !form.formState.isValid || !sourceLayer;

  return (
    <AnalysisDialogShell
      title={
        <span className='flex items-center gap-2'>
          <Droplets className='h-5 w-5 text-sky-600' />
          {t('rainfallAnalysis.dialogTitle')}
        </span>
      }
      description={
        <>
          {t('rainfallAnalysis.dialogIntro')}
        </>
      }
      formId={formId}
      primaryLabel={mutation.isPending ? t('rainfallAnalysis.running') : t('rainfallAnalysis.run')}
      primaryDisabled={submitDisabled}
      primaryBusy={mutation.isPending}
      primaryTestId='rainfall-run'>
      <Form {...form}>
        {/* No height cap here: the shell owns it (ANALYSIS_SHELL_CLASS). */}
        <form id={formId} onSubmit={form.handleSubmit((vars) => {
            if (!sourceLayer) return;
            mutation.mutate({ vars, source: sourceLayer });
          })}>
          <div className='flex min-w-0 flex-col gap-4 px-1 py-1'>
            <AnalysisSection title={t('analysisConfig.sections.inputs')} testId='rainfall-section-inputs'>
              <InputRoleField
                id='rainfall-source-layer'
                testId='rainfall-source-layer'
                role='terrain'
                layers={sourceCandidates}
                value={sourceLayerId}
                onChange={setSourceLayerId}
                disabled={mutation.isPending}
                emptyMessage={t('analysisLauncher.picker.noVectorLayers')}
              />
            </AnalysisSection>

            <AnalysisSection title={t('analysisConfig.sections.settings')} testId='rainfall-section-settings'>
              {/* Mode tabs (TOPOLOGY default, STORM opt-in). Sits above
                  everything else because it gates the visibility of the
                  storm-only inputs below. */}
              <FormField
                control={form.control}
                name='mode'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('rainfallAnalysis.modeLabel')}</FormLabel>
                    <FormControl>
                      <div className='inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5 dark:border-stone-700 dark:bg-stone-800/40'>
                        {(['TOPOLOGY', 'STORM'] as const).map((m) => (
                          <button
                            key={m}
                            type='button'
                            onClick={() => field.onChange(m)}
                            className={`rounded px-3 py-1 text-sm transition ${
                              field.value === m
                                ? 'bg-white font-medium text-slate-900 shadow-sm dark:bg-stone-700 dark:text-stone-100'
                                : 'text-slate-600 hover:text-slate-900 dark:text-stone-400 dark:hover:text-stone-200'
                            }`}>
                            {t(`rainfallAnalysis.mode${m === 'TOPOLOGY' ? 'Topology' : 'Storm'}`)}
                          </button>
                        ))}
                      </div>
                    </FormControl>
                    <FormDescription>
                      {watchedMode === 'TOPOLOGY'
                        ? t('rainfallAnalysis.modeTopologyHint')
                        : t('rainfallAnalysis.modeStormHint')}
                    </FormDescription>
                  </FormItem>
                )}
              />

              {/* Storm-mode inputs. Only shown when mode === STORM —
                  otherwise the form would be cluttered with three numeric
                  inputs that don't affect the run. */}
              {watchedMode === 'STORM' && (
                <div className='space-y-3 rounded-md border border-sky-200/70 bg-sky-50/40 p-3 dark:border-sky-900/40 dark:bg-sky-950/20'>
                  {/* Design-storm presets: pick a return period + duration
                      chip pair to populate stormDepthMm and stormDurationH
                      in one click. Hardcoded London FEH13 table; raw
                      numeric inputs below stay editable for power users. */}
                  <StormPresetPicker
                    depthMm={form.watch('stormDepthMm')}
                    durationH={form.watch('stormDurationH')}
                    onApply={(depthMm, durationH) => {
                      form.setValue('stormDepthMm', depthMm, { shouldValidate: true });
                      form.setValue('stormDurationH', durationH, { shouldValidate: true });
                    }}
                  />
                  <div className='grid grid-cols-2 gap-3'>
                    <FormField
                      control={form.control}
                      name='stormDepthMm'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {withUnit(t('rainfallAnalysis.stormDepthLabel'), t('rainfallAnalysis.stormDepthUnit'))}
                          </FormLabel>
                          <FormControl>
                            <NumericField value={field.value} onChange={field.onChange} step='1' min='0' />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='stormDurationH'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {withUnit(t('rainfallAnalysis.stormDurationLabel'), t('rainfallAnalysis.stormDurationUnit'))}
                          </FormLabel>
                          <FormControl>
                            <NumericField value={field.value} onChange={field.onChange} step='0.25' min='0' />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name='runoffCoefficient'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('rainfallAnalysis.runoffCLabel')}</FormLabel>
                        <FormControl>
                          <NumericField value={field.value} onChange={field.onChange} step='0.05' min='0' max='1' />
                        </FormControl>
                        <FormDescription className='text-xs'>
                          {t('rainfallAnalysis.runoffCHelp')}
                        </FormDescription>
                        <div className='mt-1 flex flex-wrap gap-1'>
                          {RUNOFF_C_PRESETS.map((preset) => (
                            <button
                              key={preset.labelKey}
                              type='button'
                              onClick={() => field.onChange(preset.value)}
                              title={String(preset.value)}
                              className={`rounded-full border px-2 py-0.5 text-[11px] transition ${
                                Math.abs(field.value - preset.value) < 1e-6
                                  ? 'border-sky-400 bg-sky-100 text-sky-900 dark:border-sky-500 dark:bg-sky-900/40 dark:text-sky-100'
                                  : 'border-slate-200 text-slate-600 hover:border-slate-400 dark:border-stone-700 dark:text-stone-400 dark:hover:border-stone-500'
                              }`}>
                              {t(preset.labelKey)} · {preset.value}
                            </button>
                          ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className='text-[11px] text-slate-600 dark:text-stone-400'>
                    {t('rainfallAnalysis.stormIntensityReadout', {
                      intensity:
                        form.watch('stormDurationH') > 0
                          ? (form.watch('stormDepthMm') / form.watch('stormDurationH')).toFixed(0)
                          : '—',
                    })}
                  </div>
                </div>
              )}

              {/* Sink-handling — radio with help text. Two options with
                  meaningfully different semantics, so a radio with
                  inline descriptions reads better than a select. */}
              <FormField
                control={form.control}
                name='sinkHandling'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('rainfallAnalysis.sinkHandlingLabel')}</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className='flex flex-col gap-2'>
                        {(['BREACH', 'FILL'] as const).map((mode) => (
                          <label
                            key={mode}
                            className='flex cursor-pointer items-start gap-2 rounded-md border border-transparent p-2 hover:border-slate-200 hover:bg-slate-50 dark:hover:border-stone-700 dark:hover:bg-stone-800/40'>
                            <RadioGroupItem value={mode} id={`sink-${mode}`} className='mt-1' />
                            <div className='flex-1'>
                              <div className='text-sm font-medium'>
                                {t(`rainfallAnalysis.sink${mode === 'FILL' ? 'Fill' : 'Breach'}`)}
                              </div>
                              <div className='text-xs text-slate-600 dark:text-stone-400'>
                                {t(`rainfallAnalysis.sink${mode === 'FILL' ? 'Fill' : 'Breach'}Hint`)}
                              </div>
                            </div>
                          </label>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormDescription>{t('rainfallAnalysis.sinkHandlingHelp')}</FormDescription>
                  </FormItem>
                )}
              />

              {/* Resolution + live grid-size estimate. The estimate sits
                  directly under the input, in muted text on the happy
                  path, switching to amber + warning icon when the cap is
                  exceeded. */}
              <FormField
                control={form.control}
                name='resolutionM'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {withUnit(t('rainfallAnalysis.resolutionLabel'), t('rainfallAnalysis.resolutionUnit'))}
                    </FormLabel>
                    <FormControl>
                      <NumericField
                        id='rainfall-resolution'
                        value={field.value}
                        onChange={field.onChange}
                        step='0.1'
                        min={String(MIN_RESOLUTION_M)}
                        max={String(MAX_RESOLUTION_M)}
                      />
                    </FormControl>
                    <FormDescription>
                      {t('rainfallAnalysis.resolutionHelp')}{' '}
                      {Number.isFinite(gridEstimate.cellCount) ? (
                        gridEstimate.exceedsCap ? (
                          <span className='ml-1 inline-flex items-center gap-1 font-medium text-amber-600 dark:text-amber-400'>
                            <TriangleAlert className='h-3 w-3' />
                            {t('rainfallAnalysis.resolutionTooFine', {
                              cap: `${(CELL_CAP / 1_000_000).toLocaleString()}M`,
                            })}
                          </span>
                        ) : (
                          <span className='ml-1 text-slate-500 dark:text-stone-400'>
                            {t('rainfallAnalysis.resolutionEstimate', {
                              count: gridEstimate.cellCount,
                              cells: fmtCount(gridEstimate.cellCount),
                            })}
                          </span>
                        )
                      ) : null}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </AnalysisSection>

            <AnalysisSection title={t('analysisConfig.sections.output')} testId='rainfall-section-output'>
              <OutputSummary
                testId='rainfall-output'
                creates={[t('analysisConfig.creates.flowFeatures'), t('analysisConfig.creates.flowAccumulation')]}>
                <FormField
                  control={form.control}
                  name='name'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('analysisConfig.resultName')}</FormLabel>
                      <FormControl>
                        <Input data-testid='rainfall-name' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </OutputSummary>
            </AnalysisSection>

            {/* Advanced settings (closed by default). Min pond depth/area
                are sane-defaulted for typical surveys; the user only
                touches them when they want to filter aggressively. */}
            <AdvancedSection testId='rainfall-advanced' hasError={advancedHasError}>
              <div className='grid grid-cols-2 gap-3'>
                <FormField
                  control={form.control}
                  name='minPondDepthM'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {withUnit(t('rainfallAnalysis.minPondDepthLabel'), t('rainfallAnalysis.minPondDepthUnit'))}
                      </FormLabel>
                      <FormControl>
                        <NumericField value={field.value} onChange={field.onChange} step='0.01' min='0' />
                      </FormControl>
                      <FormDescription className='text-xs'>
                        {t('rainfallAnalysis.minPondDepthHelp')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='minPondAreaM2'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {withUnit(t('rainfallAnalysis.minPondAreaLabel'), t('rainfallAnalysis.minPondAreaUnit'))}
                      </FormLabel>
                      <FormControl>
                        <NumericField value={field.value} onChange={field.onChange} step='0.1' min='0' />
                      </FormControl>
                      <FormDescription className='text-xs'>
                        {t('rainfallAnalysis.minPondAreaHelp')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </AdvancedSection>
          </div>
        </form>
      </Form>
    </AnalysisDialogShell>
  );
}

/**
 * Two row groups of chip buttons (return period × duration) backed by
 * the FEH13 London preset table. Tracks a selected pair locally so the
 * UI highlights exactly one preset that matches the current numeric
 * inputs — when the user types a custom value, no chip is highlighted.
 */
function StormPresetPicker({
  depthMm,
  durationH,
  onApply,
}: {
  depthMm: number;
  durationH: number;
  onApply: (depthMm: number, durationH: number) => void;
}) {
  const { t } = useTranslation('project');

  // Find the preset (if any) that matches the current numeric inputs
  // exactly — used to highlight the active chip pair.
  const [activeYears, activeDuration] = (() => {
    for (const y of RETURN_YEAR_OPTIONS) {
      for (const d of DURATION_OPTIONS) {
        const p = lookupStorm(y, d);
        if (
          Math.abs(p.depthMm - depthMm) < 1e-6 &&
          Math.abs(p.durationH - durationH) < 1e-6
        ) {
          return [y as ReturnYears | null, d as DurationLabel | null] as const;
        }
      }
    }
    return [null, null] as const;
  })();

  const setReturnYears = (y: ReturnYears) => {
    // Reuse existing duration if present, else default to 60min.
    const d = activeDuration ?? '60min';
    const p = lookupStorm(y, d);
    onApply(p.depthMm, p.durationH);
  };

  const setDuration = (d: DurationLabel) => {
    // Reuse existing return period if present, else default to 10y
    // (typical highway-engineering design storm).
    const y = activeYears ?? 10;
    const p = lookupStorm(y, d);
    onApply(p.depthMm, p.durationH);
  };

  return (
    <div className='space-y-1.5'>
      <div className='flex items-center justify-between gap-2'>
        <div className='text-xs font-medium text-sky-700 dark:text-sky-400'>
          {t('rainfallAnalysis.stormPresets.label')}
        </div>
        <button
          type='button'
          onClick={() => {
            const p = lookupStorm(100, '60min');
            onApply(p.depthMm, p.durationH);
          }}
          title={t('rainfallAnalysis.stormPresets.quickDemoTooltip') as string}
          className='rounded-full border border-sky-400 bg-sky-100 px-2 py-0.5 text-[10px] font-medium text-sky-900 hover:bg-sky-200 dark:border-sky-500 dark:bg-sky-900/40 dark:text-sky-100 dark:hover:bg-sky-900/60'>
          {t('rainfallAnalysis.stormPresets.quickDemo')}
        </button>
      </div>
      <div className='space-y-1'>
        <div className='flex flex-wrap items-center gap-1'>
          <span className='mr-1 text-[11px] text-slate-600 dark:text-stone-400'>
            {t('rainfallAnalysis.stormPresets.returnPeriod')}
          </span>
          {RETURN_YEAR_OPTIONS.map((y) => (
            <button
              key={y}
              type='button'
              onClick={() => setReturnYears(y)}
              className={`rounded-full border px-2 py-0.5 text-[11px] transition ${
                activeYears === y
                  ? 'border-sky-400 bg-sky-100 text-sky-900 dark:border-sky-500 dark:bg-sky-900/40 dark:text-sky-100'
                  : 'border-slate-200 text-slate-600 hover:border-slate-400 dark:border-stone-700 dark:text-stone-400 dark:hover:border-stone-500'
              }`}>
              {t('rainfallAnalysis.stormPresets.returnYearLabel', { years: y })}
            </button>
          ))}
        </div>
        <div className='flex flex-wrap items-center gap-1'>
          <span className='mr-1 text-[11px] text-slate-600 dark:text-stone-400'>
            {t('rainfallAnalysis.stormPresets.duration')}
          </span>
          {DURATION_OPTIONS.map((d) => (
            <button
              key={d}
              type='button'
              onClick={() => setDuration(d)}
              className={`rounded-full border px-2 py-0.5 text-[11px] transition ${
                activeDuration === d
                  ? 'border-sky-400 bg-sky-100 text-sky-900 dark:border-sky-500 dark:bg-sky-900/40 dark:text-sky-100'
                  : 'border-slate-200 text-slate-600 hover:border-slate-400 dark:border-stone-700 dark:text-stone-400 dark:hover:border-stone-500'
              }`}>
              {t(`rainfallAnalysis.stormPresets.duration_${d}`)}
            </button>
          ))}
        </div>
      </div>
      <p className='text-[10px] text-slate-500 dark:text-stone-400'>
        {t('rainfallAnalysis.stormPresets.footnote')}
      </p>
    </div>
  );
}
